import { useState, useEffect } from 'react';
import api from '../../services/api';

export default function AddBatchModal({ isOpen, onClose, onSuccess, defaultCenterId }) {
  const [formData, setFormData] = useState({
    name: '',
    center_id: defaultCenterId || '',
    classes: [],
    academic_year: '',
    teacher_ids: [],
  });
  const [centers, setCenters] = useState([]);
  const [centerTeachers, setCenterTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    api.getCoachingCenters({ filter: 'active', limit: 200 })
      .then(r => setCenters(r.data.centers))
      .catch(e => setError(e.message));
  }, [isOpen]);

  useEffect(() => {
    if (!formData.center_id) { setCenterTeachers([]); return; }
    api.getCoachingCenter(formData.center_id)
      .then(r => setCenterTeachers((r.data.teachers || []).filter(t => t.is_active !== false)))
      .catch(() => setCenterTeachers([]));
  }, [formData.center_id]);

  useEffect(() => {
    if (defaultCenterId && !formData.center_id) {
      setFormData(f => ({ ...f, center_id: defaultCenterId }));
    }
  }, [defaultCenterId]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleClass = (c) => {
    setFormData(f => ({
      ...f,
      classes: f.classes.includes(c) ? f.classes.filter(x => x !== c) : [...f.classes, c],
    }));
  };

  const toggleTeacher = (tid) => {
    setFormData(f => ({
      ...f,
      teacher_ids: f.teacher_ids.includes(tid) ? f.teacher_ids.filter(x => x !== tid) : [...f.teacher_ids, tid],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.createBatch(formData);
      onSuccess();
      onClose();
      setFormData({ name: '', center_id: '', classes: [], academic_year: '', teacher_ids: [] });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-teacher-border">
          <h2 className="text-xl font-semibold text-teacher-text-primary">Add Batch</h2>
          <p className="text-sm text-teacher-text-secondary mt-1">A batch groups students under one or more teachers.</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">
              Center <span className="text-red-500">*</span>
            </label>
            <select name="center_id" value={formData.center_id} onChange={handleChange} required
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
              <option value="">Select a center...</option>
              {centers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">
              Batch Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Class 12 — JEE 2027 Morning" />
          </div>
          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">Classes</label>
            <div className="flex gap-2">
              {['11', '12'].map(c => (
                <button key={c} type="button" onClick={() => toggleClass(c)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.classes.includes(c)
                      ? 'bg-primary-600 text-white'
                      : 'bg-teacher-bg-secondary text-teacher-text-primary hover:bg-gray-200'
                  }`}>
                  Class {c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">Academic Year</label>
            <input type="text" name="academic_year" value={formData.academic_year} onChange={handleChange}
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="2026-27" />
          </div>
          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-2">
              Teachers
              <span className="text-xs font-normal text-teacher-text-secondary ml-2">
                (optional — can add later from Batch Detail)
              </span>
            </label>
            {!formData.center_id ? (
              <div className="text-sm text-teacher-text-tertiary italic">Select a center first to see available teachers</div>
            ) : centerTeachers.length === 0 ? (
              <div className="text-sm text-teacher-text-tertiary italic">No active teachers in this center</div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border border-teacher-border rounded-lg p-2">
                {centerTeachers.map(t => (
                  <label key={t.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-teacher-bg rounded">
                    <input type="checkbox" checked={formData.teacher_ids.includes(t.id)}
                      onChange={() => toggleTeacher(t.id)}
                      className="w-4 h-4 text-primary-600 rounded" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-teacher-text-primary">{t.first_name} {t.last_name}</div>
                      <div className="text-xs text-teacher-text-secondary">{t.email} · {(t.subjects || []).join(', ') || 'all subjects'}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2 border border-teacher-border-strong rounded-lg text-teacher-text-primary hover:bg-teacher-bg">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {loading ? 'Creating...' : 'Create Batch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
